import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { NavLink } from 'react-router-dom';
import { Api } from '../api';
import { ROUTES } from '../routes/routes';
import './css/ResetPasswordPage.css';
import './css/LoginPage.css'; // Import shared login page styles

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    if (!/(?=.*[a-z])(?=.*[0-9])/.test(formData.password)) {
      setError('Password must contain at least one letter and one number');
      return;
    }

    setIsLoading(true);

    try {
      await Api.post(`auth/reset-password?token=${token}`, { 
        password: formData.password 
      });
      setSuccess(true);
      // Redirect to login after 2 seconds
      setTimeout(() => navigate(ROUTES.Login()), 2000);
    } catch (error) {
      setError('Password reset failed. Link may be expired. Please request a new one.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="login-container">
        <div className="login-left-column">
          <div className="brand-container">
            <NavLink to={ROUTES.Home()} className="brand-title">
              <h1>Render FM</h1>
            </NavLink>
            <p className="brand-tagline">
              Taking Audio Visualisations up a notch
            </p>
          </div>
        </div>
        <div className="login-right-column">
          <div className="login-brand-header">
            <NavLink to={ROUTES.Home()} className="login-brand-title">
              Render FM
            </NavLink>
          </div>
          <div className="login-top-menu-mobile">
            <div className="login-top-menu-container">
              <NavLink to={ROUTES.Home()} className="login-top-menu-brand">
                Render FM
              </NavLink>
            </div>
          </div>
          <div className="reset-password-form">
            <div className="reset-password-form-header">
              <h2 className="reset-password-form-title">Invalid Reset Link</h2>
              <p className="reset-password-form-subtitle">
                This password reset link is invalid or has expired.
              </p>
            </div>
            <NavLink to={ROUTES.ForgotPassword()} className="reset-password-form-button">
              Request New Link
            </NavLink>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="login-container">
        <div className="login-left-column">
          <div className="brand-container">
            <NavLink to={ROUTES.Home()} className="brand-title">
              <h1>Render FM</h1>
            </NavLink>
            <p className="brand-tagline">
              Taking Audio Visualisations up a notch
            </p>
          </div>
        </div>
        <div className="login-right-column">
          <div className="login-brand-header">
            <NavLink to={ROUTES.Home()} className="login-brand-title">
              Render FM
            </NavLink>
          </div>
          <div className="login-top-menu-mobile">
            <div className="login-top-menu-container">
              <NavLink to={ROUTES.Home()} className="login-top-menu-brand">
                Render FM
              </NavLink>
            </div>
          </div>
          <div className="reset-password-form">
            <div className="reset-password-form-header">
              <h2 className="reset-password-form-title">Password Reset Successfully!</h2>
              <p className="reset-password-form-subtitle">
                Your password has been reset. Redirecting to login...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-left-column">
        <div className="brand-container">
          <NavLink to={ROUTES.Home()} className="brand-title">
            <img src="/white_logo.png" alt="Render FM Logo" className="brand-logo" />
            <h1>Render FM</h1>
          </NavLink>
          <p className="brand-tagline">
            Taking Audio Visualisations up a notch
          </p>
        </div>
      </div>
      <div className="login-right-column">
        <div className="login-brand-header">
          <NavLink to={ROUTES.Home()} className="login-brand-title">
            <img src="/black_logo.png" alt="Render FM Logo" className="login-brand-logo" />
            Render FM
          </NavLink>
        </div>
        <div className="login-top-menu-mobile">
          <div className="login-top-menu-container">
            <NavLink to={ROUTES.Home()} className="login-top-menu-brand">
              <img src="/pink_logo.png" alt="Render FM Logo" className="login-mobile-logo" />
              Render FM
            </NavLink>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="reset-password-form">
          <div className="reset-password-form-header">
            <h2 className="reset-password-form-title">Reset Your Password</h2>
            <p className="reset-password-form-subtitle">
              Enter your new password below
            </p>
          </div>

          <label htmlFor="password" className="reset-password-form-label">
            New Password
          </label>
          <input
            id="password"
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            className="reset-password-form-input"
            placeholder="At least 8 characters"
            required
            disabled={isLoading}
          />

          <label htmlFor="confirmPassword" className="reset-password-form-label">
            Confirm Password
          </label>
          <input
            id="confirmPassword"
            type="password"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            className="reset-password-form-input"
            placeholder="Re-enter your password"
            required
            disabled={isLoading}
          />

          <div className="password-requirements">
            <p>Password must:</p>
            <ul>
              <li className={formData.password.length >= 8 ? 'valid' : ''}>
                Be at least 8 characters long
              </li>
              <li className={/[a-z]/.test(formData.password) ? 'valid' : ''}>
                Contain at least one letter
              </li>
              <li className={/[0-9]/.test(formData.password) ? 'valid' : ''}>
                Contain at least one number
              </li>
              <li className={formData.password && formData.password === formData.confirmPassword ? 'valid' : ''}>
                Match the confirmation
              </li>
            </ul>
          </div>

          {error && <p className="reset-password-form-error">{error}</p>}

          <button 
            type="submit" 
            className="reset-password-form-button" 
            disabled={isLoading}
          >
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </button>

          <div className="reset-password-form-footer">
            <NavLink to={ROUTES.Login()} className="reset-password-form-link">
              Back to Login
            </NavLink>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ResetPasswordPage;

